import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Row, Col, Spinner, Form } from 'react-bootstrap';
import _ from 'lodash';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import DateTimePicker from 'components/inputs/DateTimePicker';
import AlertItem from 'components/general/AlertItem';
import Footer from 'components/general/Footer';
import ReactPagination from 'components/general/ReactPagination';
import { SkeletonTheme } from 'react-loading-skeleton';
import { FETCH_ALERTS, DOWNLOAD_ALERTS, SET_SELECTED_ALERT_TYPES, SET_ALERT_REALTIME_ACTIVE } from 'actions/alerts';
import pop from 'assets/audio/Pop.mp3';
import { SiGooglesheets } from 'react-icons/si';
// import { AiFillCaretDown } from 'react-icons/ai';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import { getTimezoneFromCountryCode } from 'utils/timezone/timezoneUtils';
import Select from 'components/inputs/Select';
// Configuration options
const ALERT_TYPES = [
  { label: 'All Alerts', value: 'All' },
  { label: 'Low Wallet Balance', value: 'Low Wallet Balance' },
  { label: 'Power Loss', value: 'Power Loss' },
  { label: 'Emergency Button Pressed', value: 'Emergency Button Pressed' },
  { label: 'WebSocket Disconnected', value: 'Websocket Disconnected' },
  { label: 'Voltage Variance', value: 'Voltage Variance' },
  { label: 'Door Open', value: 'Door Open' },
  { label: 'Firmware Update', value: 'Firmware Update' },
  { label: 'Zero Unit Consumed', value: '0 unit consumed' },
  { label: 'Invoice on Hold', value: 'Invoice on Hold' },
  { label: 'Faulty Value Received', value: 'Faulty Value Received' },
  { label: 'Incorrect Attempts', value: 'Incorrect Attempt' },
  { label: 'Charging Interruption', value: 'Charging Interruption' },
  { label: 'Repeated OTP Requests', value: 'Repeated OTP Requests' },
  { label: 'Payment Failed', value: 'Payment Failed' },
  { label: 'User No Show', value: 'User No Show' },
  { label: 'First Session Failed', value: 'First Session Failed' },
  { label: 'Open Session', value: 'Open Session' },
  { label: 'Charger Fault', value: 'Charger Fault' },
  { label: 'Preparing → Finishing', value: 'Preparing to Finishing' },
  { label: 'Heat Issue', value: 'Heat Issue' },
  { label: 'Under Maintenance', value: 'Under Maintenance' },
  { label: 'Power Loss at Booked Charger', value: 'Power Loss at Booked Charger' },
  { label: 'Frequent Websocket Disconnect', value: 'Frequent Websocket Disconnect' },
];

const Alerts = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const audioRef = useRef(new Audio(pop));

  const { page, total: totalData, limit, totalPages, isLoading: isLoader, allowedAlertTypes, selectedAlertTypes } = useSelector((state) => state.alerts);
  const currentUserName = useSelector((state) => _.get(state, 'profile.userProfile.name', ''));
  const tenantId = useSelector((state) => _.get(state, 'profile.userProfile.tenant.id', ''));
  const countryCode = useSelector((state) => _.get(state, 'profile.country_code', 'UTC'));
  const firebaseTopics = useSelector((state) => _.get(state, 'profile.userProfile.firebase_topics', []));
  const hasAlertsAccess = firebaseTopics.includes('alerts');

  // Local state
  const [localAlerts, setLocalAlerts] = useState([]);
  const [liveModeAlerts, setLiveModeAlerts] = useState([]);
  const countryTimezone = getTimezoneFromCountryCode(countryCode);
  const [startDate, setStartDate] = useState(moment(new Date()).tz(countryTimezone).startOf('day'));
  const [endDate, setEndDate] = useState(moment(new Date()).tz(countryTimezone).endOf('day'));
  const selectedTypes = selectedAlertTypes;
  const setSelectedTypes = useCallback((val) => dispatch({ type: SET_SELECTED_ALERT_TYPES, payload: val }), [dispatch]);

  const [hover, setHover] = useState(false);
  const isRealTimeActive = useSelector((state) => state.alerts.isRealTimeActive);
  const setIsRealTimeActive = useCallback((val) => dispatch({ type: SET_ALERT_REALTIME_ACTIVE, payload: val }), [dispatch]);
  const [liveUpdates, /*setLiveUpdates*/] = useState({});
  const [newAlerts, setNewAlerts] = useState(new Set());
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Create immediate update function
  const handleRealtimeUpdate = useCallback((data) => {
    if (isLiveRef.current) {
      setLiveModeAlerts(prevAlerts => {
        const existingIndex = prevAlerts.findIndex(alert => alert.id === data.id);
        const timestamp = Date.now();

        if (existingIndex !== -1) {
          const newAlerts = [...prevAlerts];
          newAlerts[existingIndex] = {
            ...newAlerts[existingIndex],
            ...data,
            timestamp
          };
          return newAlerts.sort((a, b) => b.timestamp - a.timestamp);
        }

        // Only add new alerts if they match current filters
        const newAlert = { ...data, timestamp };
        const filteredAlerts = filterAlerts([newAlert]);

        if (filteredAlerts.length > 0) {
          return [newAlert, ...prevAlerts];
        }

        return prevAlerts;
      });
    }

    setLastUpdate(Date.now());
  }, [selectedTypes, startDate, endDate]);

  // Add new ref for tracking live status
  const isLiveRef = useRef(isRealTimeActive);
  const webSocketRef = useRef(null);
  // Update live toggle effect
  useEffect(() => {
    isLiveRef.current = isRealTimeActive;
  }, [isRealTimeActive]);



  const getActiveTypes = () => {
    return selectedTypes.map((t) => (t?.value || t)).filter((t) => t && t !== 'All');
  };

  const getAlerts = useCallback(
    (value = {}, isExcel = false) => {
      const activeTypes = getActiveTypes();
      const baseParams = {
        from: moment(startDate).tz(countryTimezone).utc().format(),
        to: moment(endDate).tz(countryTimezone).utc().format(),
        tenantId,
        ...(activeTypes.length === 1 && { alertType: activeTypes[0] }),
        ...(activeTypes.length > 1 && { alertTypes: activeTypes }),
        ...(isExcel && { excel: true }),
        ...value,
      };

      if (baseParams.excel) {
        dispatch({
          type: DOWNLOAD_ALERTS.REQUEST,
          payload: baseParams,
          cb: (res) => {
            if (res && _.get(res, 'status') === 200) {
              fileDownload(res.data, `${baseParams.from} to ${baseParams.to} Alerts.xlsx`);
              toast.success('File Downloaded Successfully');
            }
          },
        });
      }
      else {
        dispatch({
          type: FETCH_ALERTS.REQUEST,
          payload: baseParams,
          cb: (res) => {
            if (res && _.get(res, 'status') === 200) {
              if (isLiveRef.current) {
                // In live mode, store the initial data separately
                setLiveModeAlerts(res.data.results);
              } else {
                // In normal mode, update localAlerts
                setLocalAlerts(res.data.results);
              }
            }
          }
        });
      }
    },
    [dispatch, startDate, endDate, isRealTimeActive, tenantId, selectedTypes, countryTimezone]
  );

  // Real-time updates handling
  const handleNewAlert = useCallback(
    (newAlert) => {
      // Check if the alert is a valid object
      if (!_.isObject(newAlert)) {
        console.warn('Received invalid alert:', newAlert);
        return;
      }

      // Check if tenant ID matches the current user's tenant ID
      if (newAlert.tenantId === tenantId) {
        // Only add new alerts if they match current filters
        const filteredAlerts = filterAlerts([newAlert]);

        if (filteredAlerts.length > 0) {
          if (isLiveRef.current) {
            setLiveModeAlerts((prev) => [newAlert, ...prev]);
          } else {
            setLocalAlerts((prev) => [newAlert, ...prev]);
          }

          if (hasAlertsAccess && selectedTypes.length > 0) {
            // Show in-app toast notification
            const toastType = newAlert.color === 'red' ? 'error' : newAlert.color === 'yellow' ? 'warning' : 'success';
            toast(newAlert.message || 'New alert received', {
              autoClose: 6000,
              type: toastType,
              position: 'top-right',
            });

            if (Notification.permission === 'granted') {
              new Notification('New Alert', {
                body: newAlert.message || 'You have a new alert!',
              });
            }

            if (newAlert.color === 'red') {
              audioRef.current?.play().catch(console.error);
            }
          }
        }
      }
    },
    [tenantId, selectedTypes, startDate, endDate]
  );

  useEffect(() => {
    if (isRealTimeActive) {
      // When live mode is activated, fetch the complete filtered data
      const activeTypes = getActiveTypes();
      const filters = {
        page: 1,
        ...(activeTypes.length === 1 && { alertType: activeTypes[0] }),
        ...(activeTypes.length > 1 && { alertTypes: activeTypes }),
        ...(startDate &&
          endDate && {
          from: moment(startDate).tz(countryTimezone).utc(),
          to: moment(endDate).tz(countryTimezone).utc(),
        }),
      };
      getAlerts(filters);
    } else {
      // Clear live mode alerts when live mode is turned off
      setLiveModeAlerts([]);
    }
  }, [isRealTimeActive, selectedTypes, startDate, endDate, countryTimezone]);


  // Update filters effect
  useEffect(() => {
    const activeTypes = getActiveTypes();
    const filters = {
      ...(activeTypes.length === 1 && { alertType: activeTypes[0] }),
      ...(activeTypes.length > 1 && { alertTypes: activeTypes }),
      ...(startDate &&
        endDate && {
        from: moment(startDate).tz(countryTimezone).utc(),
        to: moment(endDate).tz(countryTimezone).utc(),
      }),
      // ...advanceFilterData,
    };

    getAlerts({ ...filters, page: 1 });
  }, [getAlerts, selectedTypes, startDate, endDate]);

  // Pagination handler
  const handlePageClick = (page) => {
    const activeTypes = getActiveTypes();
    const filters = {
      page: page.selected + 1,
      ...(activeTypes.length === 1 && { alertType: activeTypes[0] }),
      ...(activeTypes.length > 1 && { alertTypes: activeTypes }),
      ...(startDate &&
        endDate && {
        from: moment(startDate).tz(countryTimezone).utc(),
        to: moment(endDate).tz(countryTimezone).utc(),
      }),
    };
    getAlerts(filters);
  };

  const downloadFile = () => {
    getAlerts({}, true); // true enables excel mode
  };

  // Add transformation function
  const transformAllowedTypes = (types) => {
    if (types.length === 0) return ALERT_TYPES;
    return types.map(type => ({
      label: type,
      value: type
    }));
  };

  useEffect(() => {
    if (isRealTimeActive) {
      const ws = new WebSocket(`${process.env.REACT_APP_LIVE_CHARGING_SESSION}?tenant=${tenantId}`);

      ws.onmessage = (event) => {
        const { data } = JSON.parse(event.data);
        if (!data?.id) return;

        // If it's an alert, handle it specially
        if (data.alertType) {
          const activeTypes = getActiveTypes();
          if (activeTypes.length === 0 || activeTypes.map((t) => t.toLowerCase()).includes(data.alertType.toLowerCase())) {
            handleNewAlert(data);
          }
        } else {
          // Otherwise it's a charger update
          handleRealtimeUpdate(data);
        }

        if (data.is_new_alert) {
          setNewAlerts(prev => new Set(prev).add(data.id));
          setTimeout(() => {
            setNewAlerts(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.id);
              return newSet;
            });
          }, 500);
        }
      };

      webSocketRef.current = ws;

      return () => {
        ws.close();
        webSocketRef.current = null;
      };
    }
  }, [isRealTimeActive, handleRealtimeUpdate, handleNewAlert]);

  // Add update effect
  useEffect(() => {
    if (lastUpdate && isRealTimeActive) {
      // Force re-render of alert list
      setLocalAlerts(prev => [...prev].sort((a, b) => b.timestamp - a.timestamp));
    }
  }, [lastUpdate, isRealTimeActive]);

  // Add filterAlerts function
  const filterAlerts = (alerts) => {
    const activeTypes = getActiveTypes();
    return alerts.filter(alert => {
      if (activeTypes.length > 0) {
        const alertType = (alert.alertType || alert.type || '').toLowerCase();
        if (!activeTypes.some((t) => t.toLowerCase() === alertType)) {
          return false;
        }
      }
      if (startDate && endDate && alert.createdAt) {
        const alertTime = moment(alert.createdAt);
        if (!alertTime.isBetween(moment(startDate), moment(endDate), undefined, '[]')) {
          return false;
        }
      }
      return true;
    });
  };

  return (
    <LayoutContainer>
      <AdminHeader title={t('Alerts')} />
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
        <div className="page-content-wrapper scrollable">
          <div className="notification-details-page__main">
            <Card>
              <Row className='notifications-row'>
                <Col xl={3} lg={3} md={3} style={{ paddingTop: "15px" }}>
                  <Select
                    style={{ width: '100%' }}
                    isMulti
                    options={transformAllowedTypes(allowedAlertTypes)}
                    value={selectedTypes}
                    onChange={(val) => setSelectedTypes(val || [])}
                    placeholder="Select Alert Type(s)"
                    closeMenuOnSelect={false}
                    isClearable={selectedTypes.length > 0}
                  />
                </Col>
                <Col xl={'auto'} md={'auto'}>
                  <DateTimePicker
                    onChangeOfStartDate={setStartDate}
                    onChangeOfEndDate={setEndDate}
                    initialValueOfStartDate={moment().startOf('day')}
                    initialValueOfEndDate={moment().endOf('day')}
                  />
                </Col>
                <Col xl="auto" md="auto">
                  {isLoader ? (
                    <div className="spinner-wrapper">
                      <Spinner animation="border" variant="success" size="sm" />
                    </div>
                  ) : (
                    <div
                      className="download-icon-wrapper"
                      onClick={downloadFile}
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                    >
                      <div className={`icon icon-excel ${hover ? 'exit' : 'enter'}`}>
                        <SiGooglesheets size={30} color="#5dad5d" />
                      </div>
                      <div className={`icon icon-download ${hover ? 'enter' : 'exit'}`}>
                        <FiDownload size={30} color="#5dad5d" />
                      </div>
                    </div>
                  )}
                </Col>
                <Col xl={2} lg={2} md={2}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Form.Check
                      type="switch"
                      id="realtime-switch"
                      label="Live"
                      style={{ marginTop: '10px' }}
                      checked={isRealTimeActive}
                      onChange={(e) => setIsRealTimeActive(e.target.checked)}
                    />
                    <FiRefreshCw
                      onClick={() => !isRealTimeActive && getAlerts({ page: page })}
                      style={{
                        cursor: isRealTimeActive ? 'not-allowed' : 'pointer',
                        marginTop: '10px',
                        marginLeft: '20px',
                        opacity: isRealTimeActive ? 0.5 : 1
                      }}
                      title={isRealTimeActive ? "Disabled during live updates" : "Refresh data"}
                    />
                  </div>
                </Col>
              </Row>
              <hr />
              <div className="general-notification__main">
                {isLoader ? (
                  <div className="loader--block">Loading...</div>
                ) : _.isEmpty(isRealTimeActive ? liveModeAlerts : localAlerts) ? (
                  <div className="empty-data-block">{t('No Data')}</div>
                ) : (
                  filterAlerts(isRealTimeActive ? liveModeAlerts : localAlerts).map((alert, index) => {
                    const alertId = _.get(alert, 'id', '');
                    const updatedAlert = liveUpdates[alertId]
                      ? { ...alert, ...liveUpdates[alertId] }
                      : alert;

                    // Ensure message field exists (fallback to alertType if missing)
                    if (!updatedAlert.message && updatedAlert.alertType) {
                      updatedAlert.message = `${updatedAlert.alertType} alert`;
                    }

                    const isNew = newAlerts.has(alertId);

                    return (
                      <div
                        key={`notification-${index}`}
                        className={isNew ? 'fade-in' : ''}
                      >
                        <AlertItem alert={updatedAlert} />
                      </div>
                    );
                  })
                )}
              </div>
              {!isLoader && !isRealTimeActive && !_.isEmpty(localAlerts) && (
                <ReactPagination
                  currentPage={page}
                  limit={limit}
                  total={totalData}
                  handlePageClick={handlePageClick}
                  totalPages={totalPages}
                  marginPagesDisplayed={1}
                />
              )}
            </Card>
          </div>
        </div>
      </SkeletonTheme>
      {currentUserName !== 'CZ Tenant Admin' && <Footer />}
    </LayoutContainer >
  );
};

export default Alerts;